import { Factory, Repeat } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type {
  Order,
  OrderWorkflow,
  OrderWorkflowStage,
  OrderWorkflowSubStep,
} from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useOrderWorkflow } from '../hooks/use-order-workflow';
import { useEnableStage, useStartStage } from '../hooks/use-workflow-mutations';
import type { StageAction } from '../schemas/stage-rules';

import { ChangeRequestsSection } from './change-requests-section';
import { CompleteStageDialog } from './complete-stage-dialog';
import { DueDateDialog } from './due-date-dialog';
import { ParcelsDialog } from './parcels-dialog';
import { QcFormDialog } from './qc-form-dialog';
import { SendStageDialog } from './send-stage-dialog';
import { SkipStageDialog } from './skip-stage-dialog';
import { StageItem } from './stage-item';
import { StageNoteDialog } from './stage-note-dialog';
import type { StageTarget } from './stage-target';
import { SwitchWorkflowDialog } from './switch-workflow-dialog';

type DialogKind = 'complete' | 'skip' | 'due' | 'note' | 'send' | 'qc' | 'parcels';

const DIALOG: Partial<Record<StageAction, DialogKind>> = {
  complete: 'complete',
  deliver: 'complete',
  recordAnswer: 'complete',
  skip: 'skip',
  due: 'due',
  note: 'note',
  sendFiles: 'send',
  sendReview: 'send',
  qc: 'qc',
  parcels: 'parcels',
};

const WORKFLOW_TONE: Record<OrderWorkflow['status'], string> = {
  active: 'bg-primary/10 text-foreground border-transparent',
  completed: 'bg-success/15 text-success border-transparent dark:bg-success/20',
  stopped: 'bg-destructive/10 text-destructive border-transparent',
};

export interface OrderProductionCardProps {
  storeId: string;
  order: Order;
}

/**
 * The order's production workflow: every stage with its state, due time and actions, the
 * customer's change requests and a switch to another template. Paid orders without a workflow
 * (no default template when they were paid) can be given one here. The design approval and hold
 * state of the order is spelled out because the shipping stage cannot ship until both clear.
 */
export function OrderProductionCard({ storeId, order }: OrderProductionCardProps) {
  const { t } = useTranslation('production');
  const format = useFormat();
  const { can } = usePermissions();
  const paid = order.financialStatus === 'paid' && order.status !== 'cancelled';
  const canSwitch = can('orders.write');
  const awaitingApproval = order.approvalStatus !== null && order.approvalStatus !== 'approved';
  const onHold = order.fulfillmentStatus === 'on_hold';
  const query = useOrderWorkflow(storeId, order.id, order.workflow !== null || paid);
  const start = useStartStage(storeId, order.id);
  const enable = useEnableStage(storeId, order.id);
  const [dialog, setDialog] = useState<{ kind: DialogKind; target: StageTarget } | null>(null);
  const [switching, setSwitching] = useState(false);
  const workflow = query.data;
  const missing = isApiError(query.error) && query.error.status === 404;
  const pendingKey = start.isPending
    ? start.variables.key
    : enable.isPending
      ? enable.variables.key
      : null;

  const onAction = (action: StageAction, stage: OrderWorkflowStage, sub?: OrderWorkflowSubStep) => {
    const node = sub ?? stage;
    if (action === 'start') {
      start.mutate({ key: node.key });
      return;
    }
    if (action === 'enable') {
      enable.mutate({ key: node.key });
      return;
    }
    const kind = DIALOG[action];
    if (!kind) return;
    setDialog({
      kind,
      target: {
        storeId,
        orderId: order.id,
        key: node.key,
        name: node.name,
        type: sub ? 'task' : stage.type,
        run: node.run,
        stage,
      },
    });
  };

  const close = (open: boolean) => {
    if (!open) setDialog(null);
  };
  const lastQcFiles =
    workflow?.stages.find((s) => s.type === 'qc')?.run.qcSubmissions.at(-1)?.files ?? [];
  const stoppedAt = workflow?.stoppedStageKey
    ? (workflow.stages.find((s) => s.key === workflow.stoppedStageKey)?.name ??
      workflow.stoppedStageKey)
    : null;

  return (
    <Card role="region" aria-labelledby="production-title" data-tour="orders.detail.production">
      <CardHeader>
        <CardTitle id="production-title" className="flex items-center gap-2">
          <Factory className="size-4" aria-hidden />
          {t('card.title')}
          {workflow ? (
            <Badge variant="outline" className={WORKFLOW_TONE[workflow.status]}>
              {t(`workflowStatus.${workflow.status}`)}
            </Badge>
          ) : null}
        </CardTitle>
        {workflow ? (
          <CardDescription>{t('card.template', { name: workflow.templateName })}</CardDescription>
        ) : null}
        {canSwitch && (workflow?.status === 'active' || (missing && paid)) ? (
          <CardAction>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-tour="orders.detail.production.switch"
              onClick={() => setSwitching(true)}
            >
              <Repeat aria-hidden />
              {workflow ? t('card.switch') : t('card.attach')}
            </Button>
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {workflow?.status === 'active' && !workflow.customerLinksAvailable ? (
          <div
            role="alert"
            className="bg-destructive/10 text-destructive rounded-md border border-transparent p-3 text-sm"
          >
            {t('card.linksUnavailable')}
          </div>
        ) : null}
        {workflow?.status === 'active' && (awaitingApproval || onHold) ? (
          <div
            role="status"
            data-tour="orders.detail.production.approval"
            className="bg-warning/15 text-warning-foreground dark:bg-warning/20 flex flex-col gap-1 rounded-md border border-transparent p-3 text-sm"
          >
            {awaitingApproval && order.approvalStatus ? (
              <p>
                {t(`card.approval.${order.approvalStatus}`)}
                {workflow.designApprovalStageKey
                  ? ` ${t('card.approvalStage', {
                      stage:
                        workflow.stages.find((s) => s.key === workflow.designApprovalStageKey)
                          ?.name ?? workflow.designApprovalStageKey,
                    })}`
                  : null}
              </p>
            ) : null}
            {onHold ? <p>{t('card.onHold')}</p> : null}
          </div>
        ) : null}
        {!workflow && !paid ? (
          <p className="text-muted-foreground text-sm">{t('card.noneUnpaid')}</p>
        ) : missing ? (
          <p className="text-muted-foreground text-sm">{t('card.none')}</p>
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : !workflow ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : (
          <>
            {workflow.status === 'stopped' ? (
              <p className="text-destructive text-sm">
                {t('card.stoppedAt', {
                  stage: stoppedAt ?? '—',
                  reason: workflow.stoppedReason ?? '',
                })}
              </p>
            ) : null}
            {workflow.status === 'completed' && workflow.completedAt ? (
              <p className="text-muted-foreground text-sm" title={format.utc(workflow.completedAt)}>
                {t('card.completedAt', { when: format.dateTime(workflow.completedAt) })}
              </p>
            ) : null}
            <ol
              className="flex flex-col gap-3"
              aria-label={t('card.stagesLabel')}
              data-tour="orders.detail.production.stages"
            >
              {workflow.stages.map((stage) => (
                <StageItem
                  key={stage.key}
                  workflow={workflow}
                  stage={stage}
                  pendingKey={pendingKey}
                  lines={order.lines}
                  onAction={onAction}
                />
              ))}
            </ol>
            {workflow.history.length > 0 ? (
              <details className="text-sm">
                <summary className="text-muted-foreground cursor-pointer">
                  {t('card.history')}
                </summary>
                <ul className="mt-2 flex flex-col gap-1">
                  {workflow.history.map((item) => (
                    <li
                      key={`${item.stageKey}-${item.completedAt ?? item.status}`}
                      className="text-muted-foreground"
                    >
                      {item.stageKey} · {t(`status.${item.status}`)}
                      {item.completedAt ? ` · ${format.dateTime(item.completedAt)}` : null}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
            <div data-tour="orders.detail.production.change-requests">
              <ChangeRequestsSection
                storeId={storeId}
                orderId={order.id}
                currency={order.currency}
                workflow={workflow}
              />
            </div>
          </>
        )}
      </CardContent>
      {dialog?.kind === 'complete' ? (
        <CompleteStageDialog target={dialog.target} onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'skip' ? (
        <SkipStageDialog target={dialog.target} onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'due' ? (
        <DueDateDialog target={dialog.target} onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'note' ? (
        <StageNoteDialog target={dialog.target} onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'send' ? (
        <SendStageDialog target={dialog.target} qcFiles={lastQcFiles} onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'qc' ? <QcFormDialog target={dialog.target} onOpenChange={close} /> : null}
      {dialog?.kind === 'parcels' ? (
        <ParcelsDialog target={dialog.target} lines={order.lines} onOpenChange={close} />
      ) : null}
      {switching ? (
        <SwitchWorkflowDialog
          storeId={storeId}
          orderId={order.id}
          orderName={order.name}
          currentTemplateId={workflow?.templateId ?? null}
          onOpenChange={setSwitching}
        />
      ) : null}
    </Card>
  );
}
